import _, {values} from 'lodash';

import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {isDefined} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {AppSelector} from '../../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {useFetchWorkflowsPage} from '../../../settings/fetchers/workflows';
import {getShouldAddFirstSharedChannel} from '../../../store/data/channels/channelsSelectors';
import {getCircles} from '../../../store/data/dataSelectors';
import {getUserNamespace} from '../../../store/data/teammates/teammatesSelectors';
import {getCompanyHasSandbox, getFirstTeamUserIsAdminOf} from '../../../store/data/teams/teamsSelectors';
import {
  getIsSandboxGeneralDemoComplete,
  getShouldAddTeamInbox,
  getShouldAddTeammate,
  getShouldExploreSettings,
} from '../../../store/stateless/startGuideSelectors';

interface TaskCompletionProgressWithExploreRules {
  shouldAddTeamInbox: boolean;
  shouldAddTeammate: boolean;
  shouldExploreRules: boolean;
  numCompletedTasks: number;
  numTotalTasks: number;
}

interface LegacyTaskCompletionProgress {
  shouldCompleteSandbox: boolean;
  shouldAddTeamInbox: boolean;
  shouldAddTeammate: boolean;
  shouldAddRule: boolean | undefined;
  shouldAddCircle: boolean;
  shouldAddChannel: boolean;
  numCompletedTasks: number;
  numTotalTasks: number;
}

export function useTaskCompletionProgressWithExploreRulesV0() {
  const shouldAddTeamInbox = useAppSelector(getShouldAddTeamInbox);
  const shouldAddTeammate = useAppSelector(getShouldAddTeammate);
  const shouldExploreRules = useAppSelector(getShouldExploreSettings);

  const taskCompletionMap = {
    teamInboxTask: !shouldAddTeamInbox,
    teammateTask: !shouldAddTeammate,
    ruleTask: !shouldExploreRules,
  };

  const taskCompletionArray = values(taskCompletionMap);
  const numCompletedTasks = taskCompletionArray.filter(Boolean).length;
  const numTotalTasks = taskCompletionArray.length;

  const completionProgress: TaskCompletionProgressWithExploreRules = {
    shouldAddTeamInbox,
    shouldAddTeammate,
    shouldExploreRules,
    numCompletedTasks,
    numTotalTasks,
  };

  return completionProgress;
}

export function useLegacyTaskCompletionProgress() {
  const shouldAddTeamInbox = useAppSelector(getShouldAddTeamInbox);
  const shouldAddTeammate = useAppSelector(getShouldAddTeammate);
  const shouldAddChannel = useAppSelector(getShouldAddFirstSharedChannel);
  const shouldAddRule = useDetermineShouldAddRule();

  const circles = useAppSelector(getCircles);
  const flatCircles = _(circles).values().compact().value();
  const shouldAddCircle = flatCircles.length === 0;

  const hasSandboxAccess = useAppSelector(getCompanyHasSandbox);
  const isSandboxCompleted = useAppSelector(getIsSandboxGeneralDemoComplete);

  const taskCompletionMap = {
    teammateTask: !shouldAddTeammate,
    channelTask: !shouldAddChannel,
    sandboxTask: hasSandboxAccess ? isSandboxCompleted : undefined,
    adminWizardTask: !(shouldAddTeamInbox || shouldAddRule),
  };

  const taskCompletionArray = values(taskCompletionMap).filter(isDefined);
  const numCompletedTasks = taskCompletionArray.filter(Boolean).length;
  const numTotalTasks = taskCompletionArray.length;

  const completionProgress: LegacyTaskCompletionProgress = {
    shouldCompleteSandbox: !isSandboxCompleted && hasSandboxAccess,
    shouldAddTeamInbox,
    shouldAddTeammate,
    shouldAddRule,
    shouldAddCircle,
    shouldAddChannel,
    numCompletedTasks,
    numTotalTasks,
  };

  return completionProgress;
}

export const getAdminSetupNamespace: AppSelector<string> = (state) => {
  const firstTeamNamespace = getFirstTeamUserIsAdminOf(state)?.namespace;
  return firstTeamNamespace ?? getUserNamespace(state);
};

export function useDetermineShouldAddRule() {
  const namespace = useAppSelector(getAdminSetupNamespace);
  const {items: rules, asyncStatus} = useFetchWorkflowsPage(
    namespace,
    {offset: 0, limit: 1},
    {getWorkflowAndCustomBundle: true},
  );

  if (asyncStatus !== AsyncStatusesEnum.LOADED) {
    return undefined;
  }

  return rules.length === 0;
}
